<script lang="ts">
	import { twMerge } from 'tailwind-merge';
	import { onEventPushRoute } from '../abstract-spa-router/Router.svelte';
	import type { Snippet } from 'svelte';

	interface Props {
		/** Whether this item is the "home" (first) breadcrumb. */
		home?: boolean;
		/** Hash route to navigate to (e.g. '/Settings'). */
		href?: string | undefined;
		/** CSS class for breadcrumb links. */
		linkClass?: string;
		/** CSS class for non-link (current) breadcrumb. */
		spanClass?: string;
		/** CSS class for the home breadcrumb link. */
		homeClass?: string;
		/** Custom CSS class on the outer li. */
		class?: string;
		/** Custom CSS class for the home link. */
		classHome?: string;
		/** Custom CSS class for the link. */
		classLink?: string;
		/** Custom CSS class for the span. */
		classSpan?: string;
		/** Custom icon snippet. */
		icon?: Snippet;
		/** Default slot content. */
		children?: Snippet;
		/** Additional attributes to spread onto the li element. */
		[key: string]: any;
	}

	let {
		home = false,
		href = undefined,
		linkClass = 'ml-1 text-sm font-medium text-gray-700 hover:text-gray-900 md:ml-2 dark:text-gray-400 dark:hover:text-white',
		spanClass = 'ml-1 text-sm font-medium text-gray-500 md:ml-2 dark:text-gray-400',
		homeClass = 'inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
		class: className = undefined,
		classHome = undefined,
		classLink = undefined,
		classSpan = undefined,
		icon,
		children,
		...restProps
	}: Props = $props();
</script>

<li class={twMerge('inline-flex items-center', className)} {...restProps}>
	{#if home}
		<a
			onclick={(event: Event) => onEventPushRoute(event, href ?? '')}
			class={twMerge(homeClass, classHome)}
			{href}
		>
			{#if icon}
				{@render icon()}
			{:else}
				<svg class="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
					/>
				</svg>
			{/if}
			{@render children?.()}
		</a>
	{:else}
		{#if icon}
			{@render icon()}
		{:else}
			<svg
				class="h-6 w-6 text-gray-400"
				fill="currentColor"
				viewBox="0 0 20 20"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					fill-rule="evenodd"
					d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
					clip-rule="evenodd"
				/>
			</svg>
		{/if}

		{#if href}
			<a
				onclick={(event: Event) => onEventPushRoute(event, href ?? '')}
				class={twMerge(linkClass, classLink)}
				{href}
			>
				{@render children?.()}
			</a>
		{:else}
			<span class={twMerge(spanClass, classSpan)}>
				{@render children?.()}
			</span>
		{/if}
	{/if}
</li>

<!--
  @component
  SPA-routed breadcrumb item — wraps flowbite-svelte BreadcrumbItem with hash-based navigation.
  ## Props
  @prop home: boolean = false;
  @prop href: string | undefined = undefined;
  @prop linkClass: string;
  @prop spanClass: string;
  @prop homeClass: string;
-->
